import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';
import type { UpdateProcedureBody } from '@/types/patient-chart-api';

/**
 * PUT /api/Patients/procedure/{id}
 *
 * Shared endpoint for Edit and soft-Delete on Procedures. Wire body
 * curl-verified by Andrew 2026-05-20. See `UpdateProcedureBody` type
 * doc for the round-trip rules (most notably: `recorder: null` on
 * Update — different from Conditions / Allergies which preserve the
 * recording-user GUID).
 *
 * Cache: invalidates `['patient-procedures', patientId]`. SignalR
 * `UpdatePatientChat` "Procedure" event from PROD-827 Slice 3 also
 * fires for concurrent sessions.
 */

interface UpdateProcedureParams {
  patientId: string;
  procedureId: string;
  body: UpdateProcedureBody;
}

export function useUpdateProcedure() {
  const qc = useQueryClient();
  return useMutation<string, Error, UpdateProcedureParams>({
    mutationFn: ({ procedureId, body }) =>
      put<string>(`/api/Patients/procedure/${procedureId}`, body),
    onSuccess: (_data, { patientId }) => {
      invalidateIfNotInFlight(qc, ['patient-procedures', patientId]);
    },
  });
}
